import React, { useEffect, useState } from "react";
import { Skeleton, notification } from "antd";
import Icon from "@mdi/react";
import { mdiChevronLeft, mdiChevronRight } from "@mdi/js";
import gdBrandsApi, { GdBrand, GdBrandType } from "../../../api-services/gd-brands-api.service";
import { brandCountLabel, brandHref, brandTypeLabel } from "./components/TravelCollections";

type BrandTab = "all" | GdBrandType;

const TABS: Array<{ label: string; value: BrandTab }> = [
  { label: "All", value: "all" },
  { label: "Tours", value: "series_tour" },
  { label: "Cruises", value: "cruise" },
];

export default function GdBrandsPage() {
  const [activeTab, setActiveTab] = useState<BrandTab>("all");
  const [brands, setBrands] = useState<GdBrand[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);

    gdBrandsApi.getBrands(activeTab === "all" ? undefined : activeTab)
      .then((data) => {
        if (active) setBrands(data);
      })
      .catch(() => {
        if (!active) return;
        setBrands([]);
        notification.error({ message: "Something went wrong while loading GD Brands." });
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [activeTab]);

  const skeletonCards = Array.from({ length: 6 }, (_, index) => (
    <div className="gd-brands-page__card gd-brands-page__card--loading" key={index}>
      <Skeleton.Avatar active shape="square" size={60} />
      <Skeleton active title={{ width: "50%" }} paragraph={{ rows: 3 }} />
    </div>
  ));

  return (
    <div id="gd-brands-page">
      <section className="gd-brands-page__hero">
        <div className="gd-brands-page__inner">
          <button type="button" className="gd-brands-page__back" onClick={() => window.history.back()} aria-label="Back">
            <Icon path={mdiChevronLeft} size="22px" />
          </button>

          <h1>GD Brands</h1>

          <div className="gd-brands-page__tabs" role="tablist" aria-label="GD Brand type">
            {TABS.map((tab) => (
              <button
                type="button"
                className={`gd-brands-page__tab ${activeTab === tab.value ? "is-active" : ""}`}
                key={tab.value}
                onClick={() => setActiveTab(tab.value)}
                role="tab"
                aria-selected={activeTab === tab.value}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <main className="gd-brands-page__body">
        <div className="gd-brands-page__grid">
          {loading ? skeletonCards : brands.map((brand) => (
            <a href={brandHref(brand)} className="gd-brands-page__card" key={brand.id}>
              <span className="gd-brands-page__logo">
                {brand.logo ? <img src={brand.logo} alt={brand.title} /> : <span>{brand.title}</span>}
              </span>

              <span className="gd-brands-page__heading">
                <span className="gd-brands-page__type">{brandTypeLabel(brand)}</span>
                <strong className="gd-brands-page__title">{brand.title}</strong>
              </span>
              <span className="gd-brands-page__description">
                {brand.description || brand.subtitle || "Find holidays tailored to this travel collection."}
              </span>
              <span className="gd-brands-page__footer">
                <span className="gd-brands-page__count">{brandCountLabel(brand)}</span>
                <span className="gd-brands-page__explore">
                  Explore
                  <Icon path={mdiChevronRight} size="16px" />
                </span>
              </span>
            </a>
          ))}
        </div>

        {!loading && brands.length === 0 && (
          <div className="gd-brands-page__empty">
            No GD brands are available for this tab.
          </div>
        )}
      </main>
    </div>
  );
}
